<template>
  <div class="d-flex menu-bar justify-center align-center">
    <v-divider vertical />
    <v-select
      :items="blockTypes"
      @change="blockTypeChange"
      item-text="text"
      item-value="data"
      dense
      solo
      flat
      hide-details
      :value="blockTypeValue()"
      placeholder="Type de bloc"
      class="d-inline-block blockTypeSelect "
      color="#888"
    />
    <v-divider vertical />
    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().toggleBold().run()"
          :class="{ 'is-active': editor.isActive('bold') }"
          class="style-buttons"
        >
          <v-icon>
            format_bold
          </v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.bold') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().toggleItalic().run()"
          :class="{ 'is-active': editor.isActive('italic') }"
          class="style-buttons"
        >
          <v-icon>
            format_italic
          </v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.italic') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().toggleStrike().run()"
          :class="{ 'is-active': editor.isActive('strike') }"
          class="style-buttons"
        >
          <v-icon>
            format_strikethrough
          </v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.strikethrough') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().toggleUnderline().run()"
          :class="{ 'is-active': editor.isActive('underline') }"
          class="style-buttons"
        >
          <v-icon>
            format_underline
          </v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.underline') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().toggleHighlight().run()"
          :class="{ 'is-active': editor.isActive('highlight') }"
          class="style-buttons"
        >
          <v-icon size="18">
            border_color
          </v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.highlight') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().toggleCode().run()"
          :class="{ 'is-active': editor.isActive('code') }"
          class="style-buttons"
        >
          <v-icon>
            code
          </v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.code') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().unsetAllMarks().run()"
          class="style-buttons"
        >
          <v-icon>
            format_clear
          </v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.clear') }}</span>
    </v-tooltip>

    <v-divider vertical />
    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().toggleBulletList().run()"
          :class="{ 'is-active': editor.isActive('bulletList') }"
          class="style-buttons"
        >
          <v-icon>format_list_bulleted</v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.bulletList') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().toggleOrderedList().run()"
          :class="{ 'is-active': editor.isActive('orderedList') }"
          class="style-buttons"
        >
          <v-icon>format_list_numbered</v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.numberedList') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="editor.chain().focus().toggleBlockquote().run()"
          :class="{ 'is-active': editor.isActive('blockquote') }"
          class="style-buttons"
        >
          <v-icon>format_quote</v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.format.quote') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <div v-bind="attrs" v-on="on">
          <EditorTableMenu
            @insertTable="insertTable"
            :editor="editor"
          />
        </div>
      </template>
      <span>{{ $t('components.editor.table') }}</span>
    </v-tooltip>

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <div v-bind="attrs" v-on="on">
          <EditorImageMenu
            @insertImage="insertImage"
            :reflection-id="reflectionId"
          />
        </div>
      </template>
      <span>{{ $t('components.editor.image') }}</span>
    </v-tooltip>

    <v-divider vertical />

    <v-tooltip top class="toolbar-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          @click="toggleSpoiler"
          class="style-buttons"
          :class="{ 'is-active': editor.isActive('spoilerWrapper') }"
        >
          <v-icon>info</v-icon>
        </button>
      </template>
      <span>{{ $t('components.editor.helpBloc') }}</span>
    </v-tooltip>

    <template v-if="globalConfig.useEditorV2">
      <v-tooltip top class="toolbar-tooltip">
        <template v-slot:activator="{ on, attrs }">
          <button
            v-bind="attrs"
            v-on="on"
            @click="addJournalSection"
            class="style-buttons"
          >
            <img class="image-icon" :src="require('@/shared/assets/icons/ico-newspaper-grey.svg')">
          </button>
        </template>
        <span>{{ $t('components.editor.journal') }}</span>
      </v-tooltip>

      <v-divider vertical />

      <v-tooltip top class="toolbar-tooltip">
        <template v-slot:activator="{ on, attrs }">
          <button
            v-bind="attrs"
            v-on="on"
            @click="editor.chain().focus().setHorizontalRule().run()"
            class="style-buttons"
          >
            <v-icon>horizontal_rule</v-icon>
          </button>
        </template>
        <span>{{ $t('components.editor.horizontalRule') }}</span>
      </v-tooltip>

      <v-tooltip top class="toolbar-tooltip">
        <template v-slot:activator="{ on, attrs }">
          <button
            v-bind="attrs"
            v-on="on"
            @click="editor.chain().focus().setHardBreak().run()"
            class="style-buttons"
          >
            <v-icon>keyboard_return</v-icon>
          </button>
        </template>
        <span>{{ $t('components.editor.newline') }}</span>
      </v-tooltip>

      <v-divider vertical />

      <v-tooltip top class="toolbar-tooltip">
        <template v-slot:activator="{ on, attrs }">
          <button
            v-bind="attrs"
            v-on="on"
            @click="editor.chain().focus().undo().run()"
            class="style-buttons"
          >
            <v-icon>undo</v-icon>
          </button>
        </template>
        <span>{{ $t('components.editor.actions.undo') }}</span>
      </v-tooltip>
      <v-tooltip top class="toolbar-tooltip">
        <template v-slot:activator="{ on, attrs }">
          <button
            v-bind="attrs"
            v-on="on"
            @click="editor.chain().focus().redo().run()"
            class="style-buttons"
          >
            <v-icon>redo</v-icon>
          </button>
        </template>
        <span>{{ $t('components.editor.actions.redo') }}</span>
      </v-tooltip>

      <v-divider vertical />

      <v-tooltip top class="toolbar-tooltip">
        <template v-slot:activator="{ on, attrs }">
          <button
            v-bind="attrs"
            v-on="on"
            @click="download"
            class="style-buttons"
          >
            <v-icon>download</v-icon>
          </button>
        </template>
        <span>{{ $t('components.editor.actions.download.pdf') }}</span>
      </v-tooltip>

      <v-divider vertical />
    </template>
  </div>
</template>

<script>
import { store } from 'editor-svelte';
import { mapGetters } from 'vuex';
import { types } from '../../../../../store/rootTypes';
import EditorTableMenu from './EditorTableMenu';
import EditorImageMenu from './EditorImageMenu';
import { insertEmptyParagraphIfAtLastDocumentNode } from './TiptapExtensionUtils';

export default {
  name: 'EditorToolbar',
  props: {
    editor: {
      type: Object,
      required: true,
    },
    editorFocusedBlock: {
      type: Object,
      required: false,
      default: undefined,
    },
    reflectionId: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      globalConfig: types.globalConfig.getters.config,
    }),
  },
  components: {
    EditorTableMenu,
    EditorImageMenu,
  },
  methods: {
    insertImage(metadata) {
      this.editor.chain().focus().insertImage(metadata).run();
    },
    toggleSpoiler() {
      this.editor.commands.toggleSpoilerWrapper();
    },
    addJournalSection() {
      this.editor.commands.addJournalSection();
    },
    download() {
      store.download.download(store.download.DownloadType.PDF);
    },
    insertTable(size) {
      const { rows, cols } = size;
      // TODO extend insertTable command to move the insertEmptyParagraphIfLastDocumentNode
      // out of here, and forbid removal of the empty paragraph when its the last doc node
      // and preceded by a table
      insertEmptyParagraphIfAtLastDocumentNode(this.editor.state, this.editor.view.dispatch);
      this.editor.chain().focus().insertTable({ rows, cols, withHeaderRow: false }).run();
    },
    blockTypeChange(item) {
      this.editor.chain().focus()[item.editorAction.name](item.editorAction.opts).run();
    },
    blockTypeValue() {
      if (!this.editorFocusedBlock || !this.editorFocusedBlock.type) {
        return this.blockTypes[0];
      }
      switch (this.editorFocusedBlock.type.name) {
        case 'paragraph':
          return this.blockTypes[0];
        case 'heading': {
          const { level } = this.editorFocusedBlock.attrs;
          if (level < 7) {
            return this.blockTypes[level];
          }
          return this.blockTypes[6];
        }
        case 'codeBlock':
          return this.blockTypes[7];
        default:
          return this.blockTypes[0];
      }
    },
  },
  created() {
    this.blockTypes = [
      {
        text: this.$t('components.editor.styles.paragraph'),
        data: {
          editorAction: { name: 'setParagraph' },
          isActive: { type: 'paragraph' },
        },
      },
      {
        text: this.$t('components.editor.styles.heading1'),
        data: {
          editorAction: { name: 'toggleHeading', opts: { level: 1 } },
          isActive: { type: 'heading', opts: { level: 1 } },
        },
      },
      {
        text: this.$t('components.editor.styles.heading2'),
        data: {
          editorAction: { name: 'toggleHeading', opts: { level: 2 } },
          isActive: { type: 'heading', opts: { level: 2 } },
        },
      },
      {
        text: this.$t('components.editor.styles.heading3'),
        data: {
          editorAction: { name: 'toggleHeading', opts: { level: 3 } },
          isActive: { type: 'heading', opts: { level: 3 } },
        },
      },
      {
        text: this.$t('components.editor.styles.heading4'),
        data: {
          editorAction: { name: 'toggleHeading', opts: { level: 4 } },
          isActive: { type: 'heading', opts: { level: 4 } },
        },
      },
      {
        text: this.$t('components.editor.styles.heading5'),
        data: {
          editorAction: { name: 'toggleHeading', opts: { level: 5 } },
          isActive: { type: 'heading', opts: { level: 5 } },
        },
      },
      {
        text: this.$t('components.editor.styles.heading6'),
        data: {
          editorAction: { name: 'toggleHeading', opts: { level: 6 } },
          isActive: { type: 'heading', opts: { level: 6 } },
        },
      },
      {
        text: this.$t('components.editor.styles.preformatted'),
        data: {
          editorAction: { name: 'toggleCodeBlock' },
          isActive: { type: 'codeBlock' },
        },
      },
    ];
  },
};
</script>
<style scoped>
.menu-bar button {
  padding-left: 4px;
  padding-right: 4px;
}

.image-icon {
  width: 24px;
  display: block;
}

.blockTypeSelect {
  max-width: 175px;
  font-size: 12px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}
.style-buttons {
  margin: 2px;
  border-radius: 4px;
}
</style>

<style lang="scss">
.tableWrapper {
  padding: 1rem 0;
  overflow-x: auto;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}

.tableMenuItemBtn {
  padding: 0px 8px;
  font-size: 12px;
}

.toolbar-tooltip {
  z-index: 1000;
}

</style>
